
import { sendRequest, ResponseResult } from '../../../libs/common/HttpClient'
import { ScriptRequests } from '../../../libs/ScriptRequest'

const PROGRESS_REFRESH_RATE = 1e3

interface ProgressLoadedParams {
  chash: string
  fileTransferSessionId: string
  fileTransferId: string
  cmdUrl: string
}

interface FileTransferRequestConstructorParams {
  sendFileParams: FormData
  progressLoadedParams: ProgressLoadedParams
  actionUrl: string
  onStartLoading: () => void
  onEndLoading: (success: boolean) => void
  onSetLoadStatus: (uploaded: string) => void
  onError: (error: Error) => void
}

interface ProgressResult {
  state: 'active' | 'blocked' | 'ended' | 'error' | 'missing'
  size: string
  uploaded: string
}

interface FileTransferRequestState {
  progressErrorCounter: number
  progressNetworkErrorCounter: number
}

interface FileTransferRequestsParams {
  sendFileParams: FormData
  progressLoadedParams: {
    fileUploadProgress: {
      accountNameOrHash: string
      fileTransferSessionId: string
      fileTransferId: string
    }
  }
  sendFileActionUrl: string
  progressLoadedActionUrl: string
}

class FileTransferRequests {
  private state: FileTransferRequestState
  private params: FileTransferRequestsParams
  private xhrSendFile: XMLHttpRequest | null
  private progressRequest: ScriptRequests
  private requestInterval: ReturnType<typeof setInterval> | undefined
  private onStartLoading: () => void
  private onEndLoading: (success: boolean) => void
  private onSetLoadStatus: (uploaded: string) => void
  private onError: (error: Error) => void

  constructor({
    sendFileParams,
    progressLoadedParams,
    actionUrl,
    onStartLoading,
    onEndLoading,
    onSetLoadStatus,
    onError,
  }: FileTransferRequestConstructorParams) {
    this.state = {
      progressErrorCounter: 0,
      progressNetworkErrorCounter: 0,
    }

    this.params = {
      sendFileParams,
      progressLoadedParams: {
        fileUploadProgress: {
          accountNameOrHash: progressLoadedParams.chash,
          fileTransferSessionId: progressLoadedParams.fileTransferSessionId,
          fileTransferId: progressLoadedParams.fileTransferId,
        },
      },
      sendFileActionUrl: actionUrl,
      progressLoadedActionUrl: `${progressLoadedParams.cmdUrl}/file-transfer/progress-rest/`,
    }

    this.xhrSendFile = null
    this.progressRequest = new ScriptRequests()
    this.requestInterval = undefined

    this.onStartLoading = onStartLoading
    this.onEndLoading = onEndLoading
    this.onSetLoadStatus = onSetLoadStatus
    this.onError = onError

    this.handleSendFile()
  }

  continueProgressInNewComponent = ({
    onStartLoading = () => {},
    onEndLoading = () => {},
    onSetLoadStatus = () => {},
    onError = () => {},
  }) => {
    this.onStartLoading = onStartLoading
    this.onEndLoading = onEndLoading
    this.onSetLoadStatus = onSetLoadStatus
    this.onError = onError
  }

  unsubscribe(): void {
    this.onStartLoading = () => {}
    this.onEndLoading = () => {}
    this.onSetLoadStatus = () => {}
  }

  abort = (): void => {
    this.xhrSendFile?.abort()
    this.progressRequest.abort()
    clearInterval(this.requestInterval)
  }

  handleSendFile = (): void => {
    this.onStartLoading()
    this.xhrSendFile = sendRequest({
      url: this.params.sendFileActionUrl,
      method: 'POST',
      body: this.params.sendFileParams,
      contentType: 'multipart/form-data',
    })

    this.handleProgressLoadedChecking()
  }

  handleProgressLoadedChecking = (): void => {
    const progressLoadedRequest = (): void => {
      const onProgressError = (serverError?: any): void => {
        this.state.progressErrorCounter++
        if (this.state.progressErrorCounter > 10) {
          this.onEndLoading(false)
          clearInterval(this.requestInterval)
        } else if (serverError?.message?.includes('Network error')) {
          this.state.progressNetworkErrorCounter++
          clearInterval(this.requestInterval)
          this.requestInterval = setInterval(progressLoadedRequest, PROGRESS_REFRESH_RATE * Math.pow(2, this.state.progressNetworkErrorCounter))
        }
        console.error('[FileTransferRequests] Progress request failed. Server response:', serverError)
      }
      const onProgressLoad = (res?: ResponseResult): void => {
        if (!res || typeof res !== 'object') {
          onProgressError(new Error('Empty response from server'))
          return
        }

        if (this.state.progressErrorCounter > 0) {
          this.state.progressErrorCounter = 0
          clearInterval(this.requestInterval)
          this.requestInterval = setInterval(progressLoadedRequest, PROGRESS_REFRESH_RATE)
        }

        const result = res as unknown as ProgressResult
        if (result.state === 'active') {
          const total = this.handleFormatBiteToMB(+result.size.replace(/,/g, ''))
          const uploadedMB = this.handleFormatBiteToMB(+result.uploaded.replace(/,/g, '')).toFixed(2)
          this.onSetLoadStatus(uploadedMB)
        } else if (result.state === 'blocked') {
          this.onEndLoading(false)
          clearInterval(this.requestInterval)
        } else if (result.state === 'ended') {
          this.onEndLoading(true)
          clearInterval(this.requestInterval)
        } else if (result.state === 'missing') {
          /* ignore */
          /* приходит в 2х случаях:
            если файл не был отправлен, что мало вероятно;
            у нас загрузка файла происходит в 2 этапа (не помню точнее), мисинг приходит пока загрузка пе прошла 1й этап;
          */
        } else { /* state === 'error' */
          onProgressError(result)
        }
      }

      // this.progressRequest.send({
      //   url: this.params.progressLoadedActionUrl,
      //   params: { params: JSON.stringify(this.params.progressLoadedParams) },
      //   onSuccess: onProgressLoad,
      //   onError: onProgressError,
      // })

      sendRequest({
        url: this.params.progressLoadedActionUrl,
        method: 'GET',
        body: { params: JSON.stringify(this.params.progressLoadedParams) },
        contentType: 'multipart/form-data',
        useConsoleError: false,
        onSuccess: onProgressLoad,
        onError: onProgressError,
      })


    }

    this.requestInterval = setInterval(progressLoadedRequest, PROGRESS_REFRESH_RATE)
  }

  handleFormatBiteToMB = (bites: number): number => +(bites / 1024 / 1024).toFixed(2)
}

export default FileTransferRequests
